import React from "react";
import { useEvent } from "../../../contexts/api";
import type {
  Event,
  CreateEventRequest,
  UpdateEventRequest,
} from "../../../api/events/Event.typed";

export default function useEvents() {
  const {
    getEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    toggleEventOnline,
    uploadEventBackground,
  } = useEvent();
  const [events, setEvents] = React.useState<Event[]>([]);
  const [loading, setLoading] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    const res = await getEvents();
    setEvents(res ?? []);
    setLoading(false);
  }, [getEvents]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const create = React.useCallback(
    async (req: CreateEventRequest): Promise<Event | undefined> => {
      const created = await createEvent(req);
      if (created) await refresh();

      return created;
    },
    [createEvent, refresh],
  );

  const update = React.useCallback(
    async (req: UpdateEventRequest): Promise<Event | undefined> => {
      const updated = await updateEvent(req);
      if (updated) await refresh();

      return updated;
    },
    [updateEvent, refresh],
  );

  const remove = React.useCallback(
    async (id: number): Promise<boolean | undefined> => {
      const ok = await deleteEvent(id);
      if (ok) await refresh();

      return ok;
    },
    [deleteEvent, refresh],
  );

  const toggleOnline = React.useCallback(
    async (eventId: number, online: boolean): Promise<boolean | undefined> => {
      const ok = await toggleEventOnline(eventId, online);
      if (ok) await refresh();

      return ok;
    },
    [toggleEventOnline, refresh],
  );

  const uploadBackground = React.useCallback(
    async (eventId: number, file: File): Promise<string | undefined> => {
      const url = await uploadEventBackground(eventId, file);
      if (url) await refresh();

      return url;
    },
    [uploadEventBackground, refresh],
  );

  return { events, loading, refresh, create, update, remove, toggleOnline, uploadBackground };
}
